import api from 'service/api'

export type CategoryMember = {
  userId: number
  fullName: string
  communityId: number
  categoryId: number
  memberRoleId: number
}

export type Category = {
  id: number
  communityId: number
  categoryName: string
  description: string
  colorId: number
  iconId: number
  forAllCommunityUsers: boolean
  categoryManagers: CategoryMember[]
  approvingManagers: CategoryMember[]
  viewers: CategoryMember[]
}

export const fetchCategories = async (baseUrl, authToken, path): Promise<Category[]> => {
  const data = await api.getCategories(baseUrl, authToken, path)
  return data
}

export const createCategory = async (baseUrl, authToken, body) => {
  return api.createCategory(baseUrl, authToken, body)
}

export const updateCategory = async (baseUrl, authToken, categoryId: number, body) => {
  return api.updateCategory(baseUrl, authToken, categoryId, body)
}

export const deleteCategory = async (baseUrl, authToken, categoryId: number) => {
  return api.deleteCategory(baseUrl, authToken, categoryId)
}

export const deleteProtocol = async (baseUrl, authToken, protocolId: number) => {
  return api.deleteProtocol(baseUrl, authToken, protocolId)
}

export const archiveProtocol = async (baseUrl, authToken, protocolId: number) => {
  return api.archiveProtocol(baseUrl, authToken, protocolId)
}

export const restoreProtocol = async (baseUrl, authToken, protocolId: number) => {
  return api.restoreProtocol(baseUrl, authToken, protocolId)
}

export const downloadProtocolFile = async (baseUrl, authToken, protocolId: number, fileId: number, fileName: string) => {
  return api.downloadProtocolFile(baseUrl, authToken, protocolId, fileId, fileName)
}

export type UserOption = {
  userId: number
  fullName: string
}

export const fetchUserList = async (baseUrl, authToken): Promise<UserOption[]> => {
  return api.getProtocolUserList(baseUrl, authToken)
}

export const fetchUserNames = async (baseUrl, authToken): Promise<UserOption[]> => {
  return api.getProtocolUserNames(baseUrl, authToken)
}

export type MainManagerEntry = {
  isMainManager: boolean
  userId: number
  protocolEmail: string
}

export const patchMainManagerWithEmail = async (baseUrl, authToken, body: MainManagerEntry[]) => {
  return api.patchMainManagerWithEmail(baseUrl, authToken, body)
}

export type ArchiveItem = {
  id: number
  title: string
  categoryName: string
  creatorName: string
  protocolDate: string
  version: string
}

export type ArchiveListResponse = {
  protocols: ArchiveItem[]
  totalCount: number
}

export type ProtocolItem = {
  id: number
  categoryId: number
  categoryName: string
  title: string
  protocolDate: string
  creatorName: string
  topics: string
  rejectReason: string
  protocolStatusId: number
  participantsCount: number
  filesCount: number
  viewsCount: number
  isRead: boolean
  isEditAll: boolean
  isEditStatus: boolean
  isDelete: boolean
  isArchive: boolean
}

export type ProtocolsListResponse = {
  protocols: ProtocolItem[]
  totalCount: number
}

export type ProtocolCountsByStatus = {
  totalCount: number
  draftCount: number
  pendingApprovalCount: number
  approvedCount: number
  rejectedCount: number
}

export const fetchProtocolCountsByStatus = async (baseUrl, authToken): Promise<ProtocolCountsByStatus> => {
  return api.fetchProtocolCountsByStatus(baseUrl, authToken)
}

export type ProtocolsListBody = {
  searchText?: string
  searchMode?: number
  categoryId?: number
  creatorName?: string
  fromDate?: string
  toDate?: string
  filterByStatus?: number
  sortProperty?: string
  sortDirection?: string
  pageNumber: number
  pageSize: number
}

export type ExchangeTokenResponse = {
  accessToken: string
  expiresInSeconds: number
}


export const fetchProtocols = async (
  baseUrl,
  authToken,
  body: ProtocolsListBody
): Promise<ProtocolsListResponse> => {
  return api.fetchProtocols(baseUrl, authToken, body)
}

export const fetchArchiveList = async (baseUrl, authToken, body: ProtocolsListBody) => {
  return api.getArchiveList(baseUrl, authToken, body)
}

export type CreateProtocolBody = {
  title: string
  categoryId: number
  protocolDate: string
  protocolStatusId: number
  participants: { userId: number; participantRole: string }[]
  topics: string
  decisions: string
  description: string
}

export const createProtocol = async (baseUrl, authToken, body: CreateProtocolBody) => {
  return api.createProtocol(baseUrl, authToken, body)
}

export type ProtocolFile = {
  id: number
  protocolId: number
  fileName: string
  fileFormat: string
  isMainFile: boolean
  uploadedDate: string
  blobPath: string
  fileSize: number
  downloadUrl: string
}

export type ProtocolViewData = {
  title: string
  version: number
  modifiedDate: string
  protocolDate: string
  creatorName: string
  categoryId: number
  categoryName: string
  participants: { participantName: string; participantRole: string }[]
  topics: string
  decisions: string
  description: string
  mainFile: ProtocolFile | null
  attachments: ProtocolFile[]
}

export const fetchProtocolForView = async (baseUrl, authToken, protocolId: number): Promise<ProtocolViewData> => {
  return api.getProtocolForView(baseUrl, authToken, protocolId)
}

export type ProtocolEditParticipant = {
  userId: number
  participantName: string
  participantRole: string
}

export type ProtocolEditData = {
  title: string
  version: number
  modifiedDate: string
  protocolDate: string
  creatorName: string
  categoryId: number
  categoryName: string
  protocolStatusId: number
  participants: ProtocolEditParticipant[]
  topics: string
  decisions: string
  description: string
  mainFile: ProtocolFile | null
  attachments: ProtocolFile[]
  isPublic: boolean
}

export const fetchProtocolForEdit = async (baseUrl, authToken, protocolId: number): Promise<ProtocolEditData> => {
  return api.getProtocolForEdit(baseUrl, authToken, protocolId)
}

export type UpdateProtocolBody = {
  title: string
  protocolDate: string
  newCategoryId: number
  protocolStatusId: number
  participants: { userId: number; participantRole: string }[]
  topics: string
  decisions: string
  description: string
  isPublic: boolean
}

export const updateProtocol = async (baseUrl, authToken, protocolId: number, body: UpdateProtocolBody) => {
  return api.updateProtocol(baseUrl, authToken, protocolId, body)
}

export type UpdateProtocolStatusBody = {
  protocolStatusId: number
  rejectReason: string
}

export const updateProtocolStatus = async (baseUrl, authToken, protocolId: number, body: UpdateProtocolStatusBody) => {
  return api.updateProtocolStatus(baseUrl, authToken, protocolId, body)
}

export type FileToUpload = { file: File; isMainFile: boolean }

export const uploadProtocolFiles = async (baseUrl, authToken, protocolId: number, files: FileToUpload[]) => {
  return api.uploadProtocolFiles(baseUrl, authToken, protocolId, files)
}

export const deleteProtocolFiles = async (baseUrl, authToken, protocolId: number, fileIds: number[], filesToUpload: FileToUpload[] = []) => {
  return api.updateProtocolFiles(baseUrl, authToken, protocolId, fileIds, filesToUpload)
}

export type CategoryAccess = {
  categoryId: number
  memberRoleId: number
}

export type UserPermissions = {
  isAdmin?: boolean
  isMainManager?: boolean
  allAvailableCategories?: CategoryAccess[]
}

export const fetchUsers = async (baseUrl, authToken): Promise<UserOption[]> => {
  return api.getProtocolUsers(baseUrl, authToken)
}

export const fetchUserPermissions = async (baseUrl, authToken): Promise<UserPermissions | null> => {
  return api.getProtocolUserPermissions(baseUrl, authToken)
}
